/**
 * Vorlesen von Chat-Nachrichten über Server-KI-Stimme (TTS).
 * Chunked Prefetch: erster Satz startet sofort, Rest wird parallel geholt.
 */

let activeAudio = null;
let speakingKey = null;
/** Erhöht bei jedem stopSpeech / neuem Start — bricht laufende Requests ab. */
let speechEpoch = 0;
const listeners = new Set();

class SpeechCancelledError extends Error {
  constructor() {
    super('Vorlesen abgebrochen');
    this.name = 'SpeechCancelledError';
    this.cancelled = true;
  }
}

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn({ speakingKey, speaking: !!speakingKey });
    } catch (_) {
      /* ignore */
    }
  });
};

export function subscribeSpeech(fn) {
  listeners.add(fn);
  fn({ speakingKey, speaking: !!speakingKey });
  return () => listeners.delete(fn);
}

export function getSpeakingKey() {
  return speakingKey;
}

export function stopSpeech() {
  speechEpoch += 1;
  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.removeAttribute('src');
      activeAudio.load();
    } catch (_) {
      /* ignore */
    }
    activeAudio = null;
  }
  speakingKey = null;
  notify();
}

/** Markdown/Code grob zu sprechbarem Text. */
export function textForSpeech(raw) {
  let t = String(raw || '');
  t = t.replace(/```[\s\S]*?```/g, ' ');
  t = t.replace(/`([^`]+)`/g, '$1');
  t = t.replace(/!\[[^\]]*\]\([^)]+\)/g, ' ');
  t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  t = t.replace(/^#{1,6}\s+/gm, '');
  t = t.replace(/^\s*[-*+]\s+/gm, '');
  t = t.replace(/^\s*\d+\.\s+/gm, '');
  t = t.replace(/(\*\*|__)(.*?)\1/g, '$2');
  t = t.replace(/(\*|_)(.*?)\1/g, '$2');
  t = t.replace(/~~(.*?)~~/g, '$1');
  t = t.replace(/\|/g, ' ');
  t = t.replace(/\s+/g, ' ').trim();
  return t.slice(0, 8000);
}

/** Kurze Abschnitte für schnellen Start (~1–2 Sätze). */
export function splitSpeechChunks(text, maxLen = 320) {
  const s = String(text || '').trim();
  if (!s) return [];
  if (s.length <= maxLen) return [s];
  const parts = [];
  let rest = s;
  while (rest.length > maxLen) {
    let cut = rest.lastIndexOf('. ', maxLen);
    if (cut < maxLen * 0.35) cut = rest.lastIndexOf('! ', maxLen);
    if (cut < maxLen * 0.35) cut = rest.lastIndexOf('? ', maxLen);
    if (cut < maxLen * 0.35) cut = rest.lastIndexOf('; ', maxLen);
    if (cut < maxLen * 0.35) cut = rest.lastIndexOf(', ', maxLen);
    if (cut < maxLen * 0.35) cut = rest.lastIndexOf(' ', maxLen);
    if (cut < maxLen * 0.35) cut = maxLen;
    else cut += 1;
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts.filter(Boolean);
}

function isCancelError(err) {
  if (!err) return false;
  if (err.cancelled || err.name === 'SpeechCancelledError' || err.name === 'AbortError') return true;
  const msg = String(err.message || err.error || err).toLowerCase();
  return (
    msg.includes('interrupted') ||
    msg.includes('canceled') ||
    msg.includes('cancelled') ||
    msg.includes('abgebrochen')
  );
}

function assertEpoch(epoch) {
  if (epoch != null && epoch !== speechEpoch) {
    throw new SpeechCancelledError();
  }
}

async function fetchTtsBlob(text, { authHeaders, epoch } = {}) {
  assertEpoch(epoch);
  const res = await fetch('/api/ai/tts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeaders || {}),
    },
    body: JSON.stringify({ text, lang: 'de' }),
  });
  assertEpoch(epoch);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `TTS HTTP ${res.status}`);
  }
  const blob = await res.blob();
  assertEpoch(epoch);
  return blob;
}

function playBlob(blob, epoch) {
  return new Promise((resolve, reject) => {
    try {
      assertEpoch(epoch);
    } catch (err) {
      reject(err);
      return;
    }
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    activeAudio = audio;
    try {
      audio.volume = 0.92;
    } catch (_) {
      /* ignore */
    }
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (activeAudio === audio) activeAudio = null;
      resolve();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      if (activeAudio === audio) activeAudio = null;
      if (epoch != null && epoch !== speechEpoch) {
        reject(new SpeechCancelledError());
        return;
      }
      reject(new Error('Audio-Wiedergabe fehlgeschlagen'));
    };
    audio.play().catch((err) => {
      URL.revokeObjectURL(url);
      if (activeAudio === audio) activeAudio = null;
      reject(err);
    });
  });
}

/**
 * Holt ersten Chunk, spielt ihn, während der nächste schon geladen wird.
 */
async function speakServerTtsPrefetch(text, { authHeaders, epoch } = {}) {
  const chunks = splitSpeechChunks(text, 280);
  if (!chunks.length) throw new Error('Kein Text zum Vorlesen');

  let nextFetch = fetchTtsBlob(chunks[0], { authHeaders, epoch });

  for (let i = 0; i < chunks.length; i += 1) {
    assertEpoch(epoch);
    const blob = await nextFetch;
    assertEpoch(epoch);
    if (i + 1 < chunks.length) {
      nextFetch = fetchTtsBlob(chunks[i + 1], { authHeaders, epoch });
    }
    await playBlob(blob, epoch);
  }
}

/**
 * @param {string} rawText
 * @param {{ key?: string, authHeaders?: object }} [opts]
 */
export async function speakChatText(rawText, opts = {}) {
  const text = textForSpeech(rawText);
  if (!text) throw new Error('Kein Text zum Vorlesen');

  const key = opts.key != null ? String(opts.key) : `speech-${Date.now()}`;
  stopSpeech();
  const epoch = speechEpoch;
  speakingKey = key;
  notify();

  try {
    await speakServerTtsPrefetch(text, { authHeaders: opts.authHeaders, epoch });
  } catch (err) {
    if (isCancelError(err) || epoch !== speechEpoch) {
      return key;
    }
    throw err;
  } finally {
    if (speakingKey === key && epoch === speechEpoch) {
      speakingKey = null;
      activeAudio = null;
      notify();
    }
  }
  return key;
}
